import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { GroupModel } from 'models';

import { UseQueryReadWriteResult } from '../types';
import { useApiClient } from './useApiClient';

export function useQueryGroup(groupId: number | null | undefined): UseQueryReadWriteResult<GroupModel> {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  // Use a valid ID check rather than conditional hook call to satisfy React Hook rules
  const validGroupId = groupId != null && groupId !== 0;
  const groupQuery = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => apiClient.groupClient.getById(groupId!),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: validGroupId, // Only run the query when we have a valid ID
  });

  const groupMutation = useMutation({
    mutationFn: async (group: GroupModel) => {
      const newGroup = {
        ...group,
      };

      if (newGroup.id === 0) {
        newGroup.id = await apiClient.groupClient.create(newGroup);
      } else {
        await apiClient.groupClient.update(newGroup);
      }

      queryClient.setQueryData(['group', newGroup.id], newGroup);

      return newGroup;
    },
  });

  return [groupQuery, groupMutation];
}

export function useQueryGroups() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['groups'],
    queryFn: () => apiClient.groupClient.getAll(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
